import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { Plus, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

export function FeatureFlagsView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [form, setForm] = useState({ label: '', flag_key: '', description: '' })
  const [submitting, setSubmitting] = useState(false)

  const { data: flags } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const { data } = await supabase
        .from('feature_flags')
        .select('*')
        .order('label')
      return data || []
    }
  })

  const { data: orgs } = useQuery({
    queryKey: ['feature-flags-orgs'],
    queryFn: async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, name')
        .not('settings->>is_platform_owner', 'eq', 'true')
        .order('name')
      return data || []
    }
  })

  async function toggleGlobal(flagId: string, enabled: boolean) {
    await supabase.from('feature_flags').update({ enabled_global: enabled }).eq('id', flagId)
    qc.invalidateQueries({ queryKey: ['feature-flags'] })
  }

  async function toggleOrg(flag: any, orgId: string, enabled: boolean) {
    const current: string[] = flag.enabled_org_ids ?? []
    const next = enabled ? [...new Set([...current, orgId])] : current.filter(id => id !== orgId)
    await supabase.from('feature_flags').update({ enabled_org_ids: next }).eq('id', flag.id)
    qc.invalidateQueries({ queryKey: ['feature-flags'] })
  }

  async function addFlag() {
    if (!form.label.trim() || !form.flag_key.trim()) return
    setSubmitting(true)
    try {
      await supabase.from('feature_flags').insert({
        label: form.label.trim(),
        flag_key: form.flag_key.trim(),
        description: form.description.trim() || null,
        enabled_global: false,
      })
      toast({ title: 'Flag created.' })
      setForm({ label: '', flag_key: '', description: '' })
      setAddOpen(false)
      qc.invalidateQueries({ queryKey: ['feature-flags'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Feature Flags</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">Roll out features globally or to specific institutions.</p>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setAddOpen(true)}>
          <Plus className="w-3.5 h-3.5" />
          Add Flag
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden divide-y">
        {(flags ?? []).length === 0 ? (
          <p className="text-justify text-sm text-muted-foreground text-center py-12">No feature flags configured yet.</p>
        ) : (flags ?? []).map((flag: any) => (
          <div key={flag.id}>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <button
                className="flex items-center gap-2 min-w-0 text-left"
                onClick={() => setExpanded(expanded === flag.id ? null : flag.id)}
              >
                {expanded === flag.id ? <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />}
                <div className="min-w-0">
                  <p className="text-justify text-sm font-medium">{flag.label}</p>
                  <p className="text-xs text-muted-foreground truncate">{flag.description ?? flag.flag_key}</p>
                </div>
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-muted-foreground">Global</span>
                <Switch checked={flag.enabled_global} onCheckedChange={v => toggleGlobal(flag.id, v)} />
              </div>
            </div>
            {expanded === flag.id && (
              <div className="px-4 pb-3 bg-muted/30">
                <p className="text-justify text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2 pt-2">
                  Per-institution overrides
                </p>
                <div className="space-y-1.5">
                  {(orgs ?? []).map((org: any) => (
                    <div key={org.id} className="flex items-center justify-between py-1">
                      <span className="text-sm">{org.name}</span>
                      <Switch
                        checked={(flag.enabled_org_ids ?? []).includes(org.id)}
                        onCheckedChange={v => toggleOrg(flag, org.id, v)}
                      />
                    </div>
                  ))}
                  {(orgs ?? []).length === 0 && (
                    <p className="text-justify text-xs text-muted-foreground">No institutions yet.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="right" className="w-[420px]">
          <SheetHeader>
            <SheetTitle>Add Feature Flag</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input
                value={form.label}
                onChange={e => {
                  const label = e.target.value
                  setForm(f => ({ ...f, label, flag_key: slugify(label) }))
                }}
                placeholder="e.g. AI Selection v2"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Flag Key</Label>
              <Input
                value={form.flag_key}
                onChange={e => setForm(f => ({ ...f, flag_key: slugify(e.target.value) }))}
                placeholder="ai_selection_v2"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What does this flag control?"
                className="resize-none"
              />
            </div>
            <Button
              className="w-full"
              disabled={submitting || !form.label.trim() || !form.flag_key.trim()}
              onClick={addFlag}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Flag
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
